import React, { useState, useEffect } from "react";
import { analyticsAPI } from "../api";
import { TrendingUp, Layers, AlertCircle, ArrowUp, ArrowDown, Minus } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ScatterChart, Scatter, ZAxis } from "recharts";
import { motion } from "framer-motion";

const CLUSTER_COLORS = ["#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#ec4899", "#14b8a6"];

export default function IssueMiningPage() {
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCluster, setSelectedCluster] = useState(null);

  useEffect(() => {
    fetchIssues();
  }, []);

  const fetchIssues = async () => {
    try {
      setLoading(true);
      const res = await analyticsAPI.issueMining({ days: 7 });
      setClusters(res.data || []);
    } catch (err) {
      console.error("Issue mining error:", err);
    } finally {
      setLoading(false);
    }
  };

  const chartData = clusters.map((c, i) => ({
    label: c.label || `Cluster ${i + 1}`,
    count: c.count || c.size || 0,
    sentiment: c.avg_sentiment || 0,
    color: CLUSTER_COLORS[i % CLUSTER_COLORS.length],
  }));

  const trendIcon = (trend) => {
    if (trend === "up" || trend === "increasing") return <ArrowUp className="w-4 h-4 text-red-500" />;
    if (trend === "down" || trend === "decreasing") return <ArrowDown className="w-4 h-4 text-green-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-2xl font-bold text-gray-900">Issue Mining</h3>
          <p className="text-gray-500 text-sm mt-1">Phát hiện vấn đề nổi bật từ hội thoại bằng AI clustering</p>
        </div>
        <button onClick={fetchIssues} className="btn-primary flex items-center space-x-2 w-fit">
          <Layers className="w-4 h-4" />
          <span>Phân tích lại</span>
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-blue-100 rounded-xl"><Layers className="w-5 h-5 text-blue-600" /></div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{clusters.length}</p>
              <p className="text-sm text-gray-500">Nhóm vấn đề</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-amber-100 rounded-xl"><TrendingUp className="w-5 h-5 text-amber-600" /></div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {clusters.filter((c) => c.trend === "up" || c.trend === "increasing").length}
              </p>
              <p className="text-sm text-gray-500">Đang tăng</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 bg-red-100 rounded-xl"><AlertCircle className="w-5 h-5 text-red-600" /></div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {clusters.filter((c) => (c.avg_sentiment || 0) < 0.4).length}
              </p>
              <p className="text-sm text-gray-500">Sentiment thấp</p>
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="card">
          <h4 className="text-base font-semibold text-gray-900 mb-4">Phân bố vấn đề</h4>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="#94a3b8" angle={-20} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
              <Tooltip contentStyle={{ borderRadius: "12px", border: "none", boxShadow: "0 4px 20px rgba(0,0,0,0.1)" }} />
              <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                {chartData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Cluster Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {clusters.map((cluster, i) => (
          <motion.div
            key={cluster.id || i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="card hover:shadow-md transition-shadow cursor-pointer"
            onClick={() => setSelectedCluster(selectedCluster?.id === cluster.id ? null : cluster)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-3">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: CLUSTER_COLORS[i % CLUSTER_COLORS.length] }}
                />
                <h5 className="font-semibold text-gray-900">{cluster.label || `Cluster ${i + 1}`}</h5>
              </div>
              <div className="flex items-center space-x-2">
                {trendIcon(cluster.trend)}
                <span className="badge badge-neutral">{cluster.count || cluster.size || 0}</span>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-3">{cluster.description || cluster.summary || "Nhóm vấn đề được phát hiện tự động"}</p>
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              <span>Sentiment: <strong className={(cluster.avg_sentiment || 0) < 0.4 ? "text-red-600" : "text-green-600"}>
                {(cluster.avg_sentiment || 0).toFixed(2)}
              </strong></span>
              {cluster.keywords && (
                <span>Keywords: {(Array.isArray(cluster.keywords) ? cluster.keywords : []).slice(0, 3).join(", ")}</span>
              )}
            </div>

            {selectedCluster?.id === cluster.id && cluster.sample_messages && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                className="mt-4 pt-4 border-t border-gray-100"
              >
                <p className="text-xs font-semibold text-gray-500 mb-2">Tin nhắn mẫu:</p>
                <ul className="space-y-1">
                  {cluster.sample_messages.slice(0, 5).map((msg, j) => (
                    <li key={j} className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2">"{msg}"</li>
                  ))}
                </ul>
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>

      {clusters.length === 0 && (
        <div className="card text-center py-12">
          <Layers className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Chưa có dữ liệu issue mining. Hệ thống sẽ tự động phân tích mỗi giờ.</p>
        </div>
      )}
    </div>
  );
}
